import * as Sentry from '@sentry/nextjs'
import { useContext, useMemo } from 'react'
import { useAsyncFn } from 'react-use'
import { config } from '../../../config'
import { ContextShell } from '../../../modules/context'
import { ReferralLink } from '../../../types/referral-links.types'
import { usePlanName } from '../../plans/use-plan-name'

interface UseFetchReferralLinks {
  error: boolean
  loading: boolean
  fetchReferralLinks(): Promise<ReferralLink[]>
}

export const useFetchReferralLinks = (): UseFetchReferralLinks => {
  const { plan } = usePlanName()
  const { user, MoisesCLI } = useContext(ContextShell)

  const userName = useMemo(() => user?.name || user?.email || '', [user])

  const domain =
    config.env === 'stage' ? 'studio-stage.moises.ai' : 'studio.moises.ai'

  const [state, fetchReferralLinks] = useAsyncFn(async (): Promise<
    ReferralLink[]
  > => {
    if (!MoisesCLI.isAuthenticated() || !user?.featureFlags) {
      return []
    }

    let result = []

    try {
      const { referralLinks } = await MoisesCLI.fetchReferralLinks()
      result = referralLinks.map((item) => ({
        ...item,
        url: `https://${domain}/login?signup&redirect=claim-referral-link/${
          item.id
        }&user=${encodeURIComponent(userName)}&plan=${plan}`
      }))
    } catch (e: any) {
      Sentry.captureMessage('Error fetching referral links', {
        level: 'error',
        extra: {
          isAuthFailed: e?.response?.errors?.some(
            (i: any) => i?.extensions?.code === 'UNAUTHENTICATED'
          ),
          codes: e?.response?.errors
            ?.map((i: any) => i?.extensions?.code)
            ?.join(', '),
          error: e
        }
      })
      throw new Error(e)
    }

    return result
  }, [MoisesCLI, user, plan])

  return {
    error: !!state.error,
    loading: state.loading,
    fetchReferralLinks
  }
}
